import { useEffect, useState } from 'react'
import { AlertCircle, Bell, CheckCircle, Loader2, Save } from 'lucide-react'
import api from '../../utils/api'

const defaultChannelCampaign = {
  enabled: true,
  minVersion: '1.3.16',
  intervalHours: 72,
  pages: {
    code: { enabled: true, title: '召唤程序员', html: '' },
    video: { enabled: true, title: 'happy horse 1.1 视频生成', html: '' },
    document: { enabled: true, title: '项目材料 PPT页面布局 AI一键生成', html: '' },
    stock: { enabled: true, title: 'AI Agent狂欢下，这份避坑名单请务必收好', html: '' },
    unclick: { enabled: true, title: '发现 DClaw 隐藏玩法', html: '' }
  }
}

export default function ChannelReminderSettingsTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [jsonText, setJsonText] = useState(JSON.stringify(defaultChannelCampaign, null, 2))

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/admin/settings')
      if (response.data.success) {
        const data = response.data || {}
        const rawValue = typeof data.reminder_channel_campaign === 'object'
          ? data.reminder_channel_campaign?.value
          : data.reminder_channel_campaign
        if (rawValue && String(rawValue).trim()) {
          try {
            const parsed = JSON.parse(String(rawValue))
            setJsonText(JSON.stringify(parsed, null, 2))
          } catch (err) {
            setJsonText(String(rawValue))
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || '获取渠道弹窗设置失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccessMsg('')

    try {
      const parsed = JSON.parse(String(jsonText || '{}'))
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new SyntaxError('invalid_shape')
      }
      const response = await api.post('/admin/settings', {
        settings: { reminder_channel_campaign: String(jsonText) }
      })
      if (response.data.success) {
        setSuccessMsg('渠道弹窗设置更新成功')
        setTimeout(() => setSuccessMsg(''), 3000)
      }
    } catch (err) {
      const message = err instanceof SyntaxError
        ? '配置 JSON 格式无效'
        : (err.response?.data?.error || '渠道弹窗设置更新失败')
      setError(message)
      setTimeout(() => setError(''), 4000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-8">
      {error ? (
        <div className="rounded-xl bg-red-50 p-4 text-red-600 dark:bg-red-900/30 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        </div>
      ) : null}

      {successMsg ? (
        <div className="rounded-xl bg-green-50 p-4 text-green-600 dark:bg-green-900/30 dark:text-green-400">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            <p>{successMsg}</p>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">渠道弹窗设置</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">针对 register_channel 为 code/video/document/stock/unclick 用户的专属弹窗配置。</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            保存配置
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-6 p-6">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                reminder_channel_campaign 配置 JSON
              </label>
              <textarea
                value={jsonText}
                onChange={(event) => setJsonText(event.target.value)}
                rows={34}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 font-mono text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                placeholder="请输入 reminder_channel_campaign 的 JSON"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                enabled：弹窗总开关；intervalHours：弹窗间隔小时数（默认 72）；minVersion：最低客户端版本。
                pages 下 code/video/document/stock/unclick 分别对应五个渠道的弹窗页面，其中 enabled 为该渠道开关（默认 true，设为 false 后该渠道不弹窗且不进旧三条弹窗路线），title 为标题，html 为完整页面源码（支持 {'{{phone}}'} 与 {'{{token}}'} 占位符）。
                注意：html 留空时该渠道弹窗会显示空白页面。
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
